/**
 * JavaScript compiler wrapper using Web Worker (same approach as CodeEditor.tsx).
 *
 * Goals:
 * - Keep a simple public API: initialize, executeCode, executeCodeWithTestCases, isReady
 * - Use the same working JavaScript execution approach as CodeEditor.tsx
 * - Handle test case validation and result comparison
 * - Provide consistent interface with cppCompiler.ts and pythonCompiler.ts
 */

interface ExecutionResult {
  success: boolean;
  output: string;
  error?: string;
  executionTime: number;
  compilationError?: string;
  runtimeError?: string;
  errorType?: 'compilation' | 'runtime' | 'execution';
}

export interface TestCase {
  input: string;
  expectedOutput: string;
  fullExecutableCode: string;
}

class JavaScriptCompiler {
  // ——— Configuration ——————————————————————————————————————————————
  private static readonly EXECUTION_TIMEOUT_MS = 5000; // Same as CodeEditor.tsx

  // ——— Runtime state ———————————————————————————————————————————————
  private isInitialized = false;

  /**
   * Initialize JavaScript compiler (no-op for compatibility, execution happens on-demand)
   */
  async initialize(): Promise<void> {
    // JavaScript execution happens on-demand, so no initialization needed
    this.isInitialized = true;
    console.log('[javascript] compiler initialized (execution happens on-demand)');
  }

  /**
   * Execute JavaScript code with input and function name
   */
  async executeCode(code: string, input: string = '', functionName: string = 'main'): Promise<ExecutionResult> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    const startTime = performance.now();

    try {
      // Build executable code that includes function call
      const executableCode = `${code}

// Execute the function with input
const _executionResult = ${functionName}(${input});
console.log(_executionResult);`;

      const result = await this.executeJavaScriptInWorker(executableCode);
      const executionTime = performance.now() - startTime;

      console.log('[javascript] executeCode result:', result);

      return {
        success: result.success,
        output: result.output,
        error: result.error,
        executionTime,
        errorType: result.error ? 'runtime' : undefined
      };

    } catch (err) {
      const executionTime = performance.now() - startTime;
      const errorMessage = err instanceof Error ? err.message : 'Execution failed';
      
      console.error('[javascript] executeCode error:', errorMessage);
      
      return {
        success: false,
        output: '',
        error: errorMessage,
        errorType: 'execution',
        executionTime,
      };
    }
  }

  /**
   * Execute JavaScript code in a Web Worker using the same approach as CodeEditor.tsx
   * Note: The code passed should already contain the function call and console.log if needed
   */
  private executeJavaScriptInWorker(code: string): Promise<{ success: boolean; output: string; error?: string }> {
    return new Promise((resolve) => {
      const output: any[] = [];

      // Create code for the worker including console.log capture (exactly like CodeEditor.tsx)
      const workerCode = `
        // Override console.log
        const originalLog = console.log;
        const logMessages = [];
        
        console.log = (...args) => {
          const message = args.map(arg => typeof arg === "object" ? JSON.stringify(arg) : String(arg)).join(" ");
          logMessages.push(message);
          self.postMessage({type: "log", content: message});
        };
        
        try {
          ${code}
          
          // Code should already contain function calls and console.log
          self.postMessage({type: "done"});
        } catch(error) {
          self.postMessage({type: "error", content: error.message});
        }
      `;

      // Create a blob URL for the worker (same as CodeEditor.tsx)
      const blob = new Blob([workerCode], { type: "application/javascript" });
      const workerUrl = URL.createObjectURL(blob);

      // Create and start the worker (same as CodeEditor.tsx)
      const worker = new Worker(workerUrl);

      // Set a timeout to kill the worker after 5 seconds (same as CodeEditor.tsx)
      const timeoutId = setTimeout(() => {
        worker.terminate();
        URL.revokeObjectURL(workerUrl);
        resolve({ 
          success: false, 
          output: '', 
          error: "Execution timed out. Your code might contain an infinite loop." 
        });
      }, JavaScriptCompiler.EXECUTION_TIMEOUT_MS);

      // Listen for messages from the worker (exactly like CodeEditor.tsx)
      worker.onmessage = (event) => {
        if (event.data.type === "done") {
          clearTimeout(timeoutId);
          worker.terminate();
          URL.revokeObjectURL(workerUrl);
          resolve({ success: true, output: output.map(item => item.content).join('\n') });
        } else if (event.data.type === "log") {
          output.push(event.data);
        }
      };

      // Handle errors (same as CodeEditor.tsx)
      worker.onerror = (error) => {
        clearTimeout(timeoutId);
        worker.terminate();
        URL.revokeObjectURL(workerUrl);
        resolve({ 
          success: false, 
          output: '', 
          error: "Worker error: " + error.message 
        });
      };
    });
  }

  /**
   * Extract function name from the user code
   */
  private extractFunctionName(userCode: string): string | null {
    // Look for function declaration patterns
    const functionMatch = userCode.match(/function\s+(\w+)\s*\(/);
    if (functionMatch && functionMatch[1]) {
      return functionMatch[1];
    }
    
    // Look for arrow function assignment patterns
    const arrowMatch = userCode.match(/(\w+)\s*=\s*\([^)]*\)\s*=>/);
    if (arrowMatch && arrowMatch[1]) {
      return arrowMatch[1];
    }
    
    // Look for const/let/var function assignment patterns
    const constMatch = userCode.match(/(?:const|let|var)\s+(\w+)\s*=\s*function/);
    if (constMatch && constMatch[1]) {
      return constMatch[1];
    }
    
    return null;
  }

  /**
   * Clean output by removing empty lines and formatting
   */
  private cleanOutput(output: string): string {
    const cleanedOutput = (output || '')
      .split(/\r?\n/)
      .map(s => s.trim())
      .filter(Boolean)
      .map(s => {
        // Convert double quotes to single quotes and add spaces around colons to match expected format
        return s.replace(/"/g, "'").replace(/:/g, ': ').replace(/,/g, ', ');
      });

    return cleanedOutput.join('\n');
  }

  /**
   * Execute code with multiple test cases
   */
  async executeCodeWithTestCases(
    userCode: string,
    testCases: TestCase[]
  ): Promise<ExecutionResult[]> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    const results: ExecutionResult[] = [];

    for (const testCase of testCases) {
      try {
        // For JavaScript, we need to create executable code
        // The userCode should be the function definition, we'll call it with the input
        const trimmedCode = userCode.trim();
        
        // Try to extract function name from userCode, fallback to 'main'
        const functionName = this.extractFunctionName(userCode) || 'main';
        console.log('[javascript] Using function name:', functionName);
        
        // Create executable code - function definition + single function call with console.log
        const codeToExecute = `${trimmedCode}

// Test the function with input (single call)
const _testResult = ${functionName}(${testCase.input});
console.log(_testResult);`;

        console.log('[javascript] Executing code:', codeToExecute);
        console.log('[javascript] Test case input:', testCase.input);
        
        const startTime = performance.now();
        const workerResult = await this.executeJavaScriptInWorker(codeToExecute);
        const executionTime = performance.now() - startTime;
        const cleanedOutput = this.cleanOutput(workerResult.output);
        
        // Convert to ExecutionResult format
        const execResult: ExecutionResult = {
          success: workerResult.success,
          output: cleanedOutput,
          error: workerResult.error,
          executionTime,
          errorType: workerResult.error ? 'runtime' : undefined
        };
        
        results.push(execResult);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Execution failed for test case';
        results.push({
          success: false,
          output: '',
          error: errorMessage,
          errorType: 'execution',
          executionTime: 0,
        });
      }
    }
    
    return results;
  }

  /**
   * Check if compiler is ready
   */
  isReady(): boolean {
    return this.isInitialized;
  }

  /**
   * Cleanup resources (no-op for this implementation)
   */
  destroy(): void {
    // No cleanup needed for this implementation
    this.isInitialized = false;
  }
}

// Export singleton
export const javascriptCompiler = new JavaScriptCompiler();
export default javascriptCompiler;
